import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  UserApplicationAccessResponseSchema,
  UserApplicationAccessListResponseSchema,
  SetUserApplicationAccessSchema,
  UpdateUserApplicationAccessSchema,
  ErrorResponseSchema,
  ApplicationAccessConfigSchema,
  ApplicationSummaryListResponseSchema,
} from './user-application-access.schema';

export const USER_APPLICATION_ACCESS_PATH = 'user-application-access';

export const UserApplicationAccessContract = terraContract
  .prefix(`/${USER_APPLICATION_ACCESS_PATH}`)
  .router({
    listUserAccess: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List all user application access configurations',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z.object({
            targetTenantId: z.string().optional(),
          }),
        }),
      )
      .output(UserApplicationAccessListResponseSchema)
      .errors({ FORBIDDEN: { status: 403, data: ErrorResponseSchema } }),
    listTenantApplications: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'GET',
        path: '/applications',
        summary:
          'List applications for a tenant (current or child) for access assignment',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z.object({
            tenantId: z.string(),
          }),
        }),
      )
      .output(ApplicationSummaryListResponseSchema)
      .errors({ FORBIDDEN: { status: 403, data: ErrorResponseSchema } }),
    getMyAccess: terraContract
      .meta({
        authenticated: true,
      })
      .route({
        method: 'GET',
        path: '/me',
        summary: 'Get current user application access configuration',
        inputStructure: 'detailed',
      })
      .output(ApplicationAccessConfigSchema),
    getUserAccess: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'GET',
        path: '/{userId}',
        summary: 'Get user application access configuration',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            userId: z.string(),
          }),
          query: z.object({
            targetTenantId: z.string().optional(),
          }),
        }),
      )
      .output(UserApplicationAccessResponseSchema)
      .errors({
        FORBIDDEN: { status: 403, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
      }),
    setUserAccess: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'PUT',
        path: '/{userId}',
        summary: 'Set user application access configuration',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            userId: z.string(),
          }),
          body: UpdateUserApplicationAccessSchema,
        }),
      )
      .output(UserApplicationAccessResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        FORBIDDEN: { status: 403, data: ErrorResponseSchema },
        UNPROCESSABLE_CONTENT: { status: 422, data: ErrorResponseSchema },
      }),
    createUserAccess: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create user application access configuration',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: SetUserApplicationAccessSchema }))
      .output(UserApplicationAccessResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        FORBIDDEN: { status: 403, data: ErrorResponseSchema },
        CONFLICT: { status: 409, data: ErrorResponseSchema },
        UNPROCESSABLE_CONTENT: { status: 422, data: ErrorResponseSchema },
      }),
    deleteUserAccess: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'DELETE',
        path: '/{userId}',
        summary: 'Delete user application access configuration',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(
        z.object({
          params: z.object({
            userId: z.string(),
          }),
          query: z.object({
            targetTenantId: z.string().optional(),
          }),
        }),
      )
      .output(z.null())
      .errors({
        FORBIDDEN: { status: 403, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
      }),
  });
